<script setup>
// Lightweight, dependency-free product tour. Renders a dark overlay with a
// "spotlight" cut-out over a target element and a tooltip with step controls.
// Steps with a `route` navigate the dashboard, then continue.
import { ref, computed, watch, onBeforeUnmount, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { useMesselloHelp } from 'dashboard/composables/useMesselloHelp';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';

const { tourActive, stopTour } = useMesselloHelp();
const { updateUISettings } = useUISettings();
const { accountId } = useAccount();
const router = useRouter();

const STEPS = [
  {
    title: 'Welcome to Messello 👋',
    text: 'Here is a quick 60-second tour of your new shared inbox. You can skip anytime.',
  },
  {
    selector: '[data-messello-tour="nav"]',
    placement: 'right',
    title: 'Your navigation',
    text: 'Your shared inbox, contacts, reports and settings all live here — one click away.',
  },
  {
    selector: '[data-messello-tour="help"]',
    placement: 'left',
    title: 'Help is always one click away',
    text: 'The ? button opens guides, lets you search the help center, restart this tour, or reach us.',
  },
  {
    route: 'settings_inbox_new',
    title: 'Connect your first channel',
    text: 'Go to Settings → Inboxes → Add inbox to connect WhatsApp, Telegram, website live chat, email, Instagram and more — all in one inbox.',
    cta: 'Open channels',
  },
  {
    route: 'general_settings_index',
    title: 'Turn on Messello AI',
    text: 'In Account settings, switch on AI to draft replies from your help center, auto-tag conversations, and summarize long threads — included, no per-message fees.',
    cta: 'Open AI settings',
  },
  {
    route: 'agent_list',
    title: 'Invite your team',
    text: 'Add teammates in Settings → Agents so conversations can be assigned and shared.',
    cta: 'Open agents',
  },
  {
    title: 'You are all set! 🎉',
    text: 'Reopen this tour or browse every guide anytime from the ? button in the bottom-right corner.',
  },
];

const step = ref(0);
const rect = ref(null);
const current = computed(() => STEPS[step.value] || {});

const PAD = 8;
const measure = () => {
  const sel = current.value.selector;
  if (!sel) {
    rect.value = null;
    return;
  }
  const el = document.querySelector(sel);
  rect.value = el ? el.getBoundingClientRect() : null;
};

const refresh = async () => {
  await nextTick();
  // give the newly-navigated screen a moment to render before measuring
  setTimeout(measure, 60);
};

watch(tourActive, active => {
  if (active) {
    step.value = 0;
    refresh();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
  } else {
    window.removeEventListener('resize', measure);
    window.removeEventListener('scroll', measure, true);
  }
});
watch(step, refresh);

const finish = () => {
  updateUISettings({ messello_tour_done: true });
  stopTour();
};
const skip = () => finish();

const goTo = route => {
  if (!route) return;
  try {
    router.push({ name: route, params: { accountId: accountId.value } });
  } catch (e) {
    // ignore navigation errors — the tour continues regardless
  }
};

const next = () => {
  if (current.value.route) goTo(current.value.route);
  if (step.value >= STEPS.length - 1) {
    finish();
  } else {
    step.value += 1;
  }
};
const prev = () => {
  if (step.value > 0) step.value -= 1;
};

// tooltip position — always clamped fully inside the viewport so controls never
// slip off-screen (the ? launcher sits at the bottom-right corner).
const TIP_W = 320;
const TIP_H = 240;
const tip = computed(() => {
  const r = rect.value;
  if (!r) {
    return { left: '50%', top: '50%', transform: 'translate(-50%,-50%)' };
  }
  const place = current.value.placement || 'bottom';
  const gap = 14;
  let left;
  let top;
  if (place === 'left') {
    left = r.left - TIP_W - gap;
    top = r.top + r.height / 2 - TIP_H / 2;
  } else if (place === 'right') {
    left = r.right + gap;
    top = r.top + 20;
  } else if (place === 'top') {
    left = r.left;
    top = r.top - gap - TIP_H;
  } else {
    left = r.left;
    top = r.bottom + gap;
  }
  const m = 12;
  left = Math.max(m, Math.min(left, window.innerWidth - TIP_W - m));
  top = Math.max(m, Math.min(top, window.innerHeight - TIP_H - m));
  return { left: `${left}px`, top: `${top}px` };
});
const spot = computed(() => {
  const r = rect.value;
  if (!r) return null;
  return {
    left: `${r.left - PAD}px`,
    top: `${r.top - PAD}px`,
    width: `${r.width + PAD * 2}px`,
    height: `${r.height + PAD * 2}px`,
  };
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', measure);
  window.removeEventListener('scroll', measure, true);
});
</script>

<template>
  <div v-if="tourActive" class="mz-tour">
    <div v-if="!spot" class="mz-tour-dim" @click="skip" />
    <div v-else class="mz-tour-spot" :style="spot" />
    <div class="mz-tour-tip" :style="tip">
      <div class="mz-tour-step">Step {{ step + 1 }} of {{ STEPS.length }}</div>
      <h3 class="mz-tour-title">{{ current.title }}</h3>
      <p class="mz-tour-text">{{ current.text }}</p>
      <div class="mz-tour-ctrls">
        <button class="mz-tour-skip" @click="skip">Skip</button>
        <div class="mz-tour-right">
          <button v-if="step > 0" class="mz-tour-back" @click="prev">Back</button>
          <button class="mz-tour-next" @click="next">
            {{ current.cta || (step >= STEPS.length - 1 ? 'Done' : 'Next') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.mz-tour {
  position: fixed;
  inset: 0;
  z-index: 10050;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}
.mz-tour-dim {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.6);
}
.mz-tour-spot {
  position: absolute;
  border-radius: 12px;
  box-shadow: 0 0 0 9999px rgba(15, 23, 42, 0.6);
  transition: all 0.2s ease;
  pointer-events: none;
}
.mz-tour-tip {
  position: absolute;
  width: 320px;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.28);
  padding: 18px 18px 14px;
}
.mz-tour-step {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #6366f1;
  margin-bottom: 6px;
}
.mz-tour-title {
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 6px;
}
.mz-tour-text {
  font-size: 14px;
  line-height: 1.5;
  color: #475569;
  margin: 0 0 14px;
}
.mz-tour-ctrls {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.mz-tour-right {
  display: flex;
  gap: 8px;
}
.mz-tour-skip,
.mz-tour-back {
  border: 0;
  background: transparent;
  color: #64748b;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  padding: 8px 10px;
  border-radius: 8px;
  font-family: inherit;
}
.mz-tour-back:hover,
.mz-tour-skip:hover {
  background: #f2f6ff;
  color: #0f172a;
}
.mz-tour-next {
  border: 0;
  background: #3b6cf6;
  color: #fff;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  padding: 8px 16px;
  border-radius: 9px;
  font-family: inherit;
}
.mz-tour-next:hover {
  background: #2a55d6;
}
</style>
